<script setup lang="ts">
/**
 * Le catalogue des types d'alarme (écran 3 du dossier), en lecture.
 *
 * Ce que l'utilisateur doit vivre : avant de créer une surveillance, il
 * parcourt ce qu'on peut surveiller chez lui, dans ses mots, le cherche, le
 * filtre par mesure et par type d'équipement, et le trie. Sur un équipement
 * désigné (`?objet_type=…&_id_objet=…`), le catalogue est filtré sur ce que
 * cet équipement mesure, et la page dit honnêtement combien de types ne sont
 * pas proposés ; sans équipement, rien n'a été compté et aucun nombre n'est
 * dit (jamais 0). Aucun bouton d'écriture : créer et demander un type d'alarme
 * viennent au lot F8.
 */
// ================================
// IMPORTS
// ================================
import { computed, reactive, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useRoute } from "vue-router"
import { useAuthStore } from "@core/stores/auth.store"
import type { Traduire } from "@modules/alarmes-v2/i18n/enregistrer"
import { useAcces } from "@modules/alarmes-v2/acces/useAcces"
import { useCatalogueStore } from "@modules/alarmes-v2/stores/catalogue.store"
import { CODE_INDISPONIBLE } from "@modules/alarmes-v2/services/appel"
import { cleDuRefus } from "@modules/alarmes-v2/utils/refus"
import { CHEMIN_SURVEILLANCES } from "@modules/alarmes-v2/utils/menu"
import {
    EQUIPEMENTS,
    FILTRES_DE_DEPART,
    catalogueVide,
    comptesDuFiltre,
    mesuresDuCatalogue,
    typesAffiches,
    type FiltresCatalogue,
} from "@modules/alarmes-v2/utils/catalogue"
import type { AlarmesV2FiltresCatalogue } from "@modules/alarmes-v2/types/alarmes-v2"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n()
const tr = t as unknown as Traduire

const route = useRoute()
const authStore = useAuthStore()
const catalogueStore = useCatalogueStore()
const { acces } = useAcces()

/** Les filtres de la page, propres à la visite. */
const filtres = reactive<FiltresCatalogue>({ ...FILTRES_DE_DEPART })

// ================================
// COMPUTED
// ================================
/** Le verdict de la matrice pour « Voir le catalogue ». */
const getAcces = computed(() => acces("catalogue_voir"))

/** L'équipement désigné par l'adresse, s'il est lisible. */
const getEquipement = computed((): AlarmesV2FiltresCatalogue => {
    const objetType = route.query.objet_type
    const idObjet = route.query._id_objet
    if (
        (objetType === "source" || objetType === "building") &&
        typeof idObjet === "string" &&
        /^[0-9a-f]{24}$/i.test(idObjet)
    )
        return { objet_type: objetType, _id_objet: idObjet }
    return {}
})

const getAffiches = computed(() =>
    typesAffiches(catalogueStore.types, filtres, tr)
)

const getMesures = computed(() => mesuresDuCatalogue(catalogueStore.types, tr))

const getTotal = computed(() => catalogueStore.types.length)

/** « 3 types d'alarme au catalogue ». */
const getTitre = computed(() =>
    getTotal.value === 1
        ? t("alarmesV2.catalogue.compteTitreUn")
        : t("alarmesV2.catalogue.compteTitre", { n: getTotal.value })
)

/** « 2 types d'alarme affichés sur 3. » */
const getCompte = computed(() =>
    getAffiches.value.length === 1
        ? t("alarmesV2.catalogue.affichesUn", { total: getTotal.value })
        : t("alarmesV2.catalogue.affiches", {
              n: getAffiches.value.length,
              total: getTotal.value,
          })
)

/** Vrai si la lecture porte sur un équipement désigné. */
const getSurUnEquipement = computed(() => !!getEquipement.value._id_objet)

/**
 * Le compte honnête de ce qui n'est pas proposé, phrase par phrase. Un
 * compteur `null` ne dit aucun nombre ; sur un équipement, il dit qu'on ne
 * sait pas (`comptesDuFiltre`).
 */
const getComptesFiltre = computed((): string[] =>
    comptesDuFiltre(
        catalogueStore.typesEcartes,
        catalogueStore.typesNonRattaches,
        getSurUnEquipement.value,
        tr
    )
)

/**
 * Aucun type montré : « rien de publié » seulement si l'on sait que rien n'a
 * été écarté ; tout écarté se dit par le compte ; un compte illisible sur un
 * équipement dit « rien de proposé pour cet équipement » (`catalogueVide`).
 */
const getVide = computed(() =>
    catalogueVide(
        getTotal.value,
        catalogueStore.typesEcartes,
        getSurUnEquipement.value
    )
)

/** La phrase d'un échec. */
const getPhraseErreur = computed((): string => {
    const e = catalogueStore.erreur
    if (!e || e.code === CODE_INDISPONIBLE)
        return t("alarmesV2.catalogue.indisponible")
    return t(cleDuRefus(e))
})

// ================================
// METHODS
// ================================
/**
 * Lit le catalogue, si l'entreprise a l'offre et que la matrice l'ouvre ;
 * relu à chaque visite, ses comptes ne sont jamais servis périmés.
 */
function lire(): void {
    if (!authStore.hasOfferEnergyManager || !getAcces.value.ok) return
    void catalogueStore.charger(getEquipement.value, { relire: true })
}

watch(
    () => [
        authStore.companyId,
        authStore.hasOfferEnergyManager,
        getAcces.value.ok,
        getEquipement.value.objet_type,
        getEquipement.value._id_objet,
    ],
    lire,
    { immediate: true }
)

/** Relit après un échec. */
function onRelire(): void {
    catalogueStore.reinitialiser()
    lire()
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <CadreModule>
        <div class="mx-auto flex max-w-7xl flex-col gap-4 p-4 md:p-6">
            <RouterLink
                :to="CHEMIN_SURVEILLANCES"
                class="text-sm text-primary-700 underline"
                >{{ t("alarmesV2.catalogue.versLaListe") }}</RouterLink
            >
            <header>
                <h1 class="text-2xl font-semibold text-surface-900">
                    {{ t("alarmesV2.catalogue.titre") }}
                </h1>
            </header>

            <p
                v-if="!getAcces.ok"
                class="text-sm text-surface-800"
                data-test="refus"
            >
                {{ getAcces.raison || t("alarmesV2.refus.neutre") }}
            </p>
            <p
                v-else-if="
                    catalogueStore.statut === 'chargement' ||
                    catalogueStore.statut === 'attente'
                "
                class="text-sm text-surface-600"
            >
                {{ t("alarmesV2.catalogue.chargement") }}
            </p>
            <div
                v-else-if="catalogueStore.statut === 'erreur'"
                class="flex flex-wrap items-center gap-3"
                data-test="erreur"
            >
                <p class="text-sm text-surface-800">{{ getPhraseErreur }}</p>
                <button
                    type="button"
                    class="text-sm font-medium text-primary-700 underline"
                    @click="onRelire"
                >
                    {{ t("alarmesV2.refus.relire") }}
                </button>
            </div>
            <template v-else>
                <!-- ================================ -->
                <!-- PRÉSENTATION ET OUTILS -->
                <!-- ================================ -->
                <section
                    class="flex flex-col gap-3 rounded-lg border border-surface-200 bg-surface-0 p-4"
                >
                    <h2
                        class="text-base font-semibold text-surface-900"
                        data-test="titre-compte"
                    >
                        {{ getTitre }}
                    </h2>
                    <p class="text-sm text-surface-700">
                        {{ t("alarmesV2.catalogue.intro") }}
                    </p>
                    <p
                        v-for="(phrase, i) in getComptesFiltre"
                        :key="i"
                        class="text-sm text-surface-700"
                        data-test="compte-filtre"
                    >
                        {{ phrase }}
                    </p>
                    <div
                        class="rounded border border-surface-200 bg-surface-50 p-3 text-sm"
                    >
                        <strong class="block">{{
                            t("alarmesV2.catalogue.natureTitre")
                        }}</strong>
                        <strong>{{
                            t("alarmesV2.catalogue.carte.aTraiter")
                        }}</strong>
                        : {{ t("alarmesV2.catalogue.natureATraiter") }}
                        <strong>{{
                            t("alarmesV2.catalogue.carte.information")
                        }}</strong>
                        : {{ t("alarmesV2.catalogue.natureInformation") }}
                    </div>
                    <div class="flex flex-wrap gap-3">
                        <label class="flex min-w-56 flex-1 flex-col text-xs">
                            {{ t("alarmesV2.catalogue.rechercher") }}
                            <input
                                v-model="filtres.texte"
                                type="search"
                                class="rounded border border-surface-300 px-2 py-1 text-sm"
                                :placeholder="
                                    t('alarmesV2.catalogue.rechercheAide')
                                "
                                data-test="recherche"
                            />
                        </label>
                        <label class="flex flex-col text-xs">
                            {{ t("alarmesV2.catalogue.filtreMesure") }}
                            <select
                                v-model="filtres.mesure"
                                class="rounded border border-surface-300 px-2 py-1 text-sm"
                                data-test="filtre-mesure"
                            >
                                <option value="tous">
                                    {{ t("alarmesV2.catalogue.toutesMesures") }}
                                </option>
                                <option
                                    v-for="m in getMesures"
                                    :key="m"
                                    :value="m"
                                >
                                    {{ m }}
                                </option>
                            </select>
                        </label>
                        <label class="flex flex-col text-xs">
                            {{ t("alarmesV2.catalogue.filtreEquipement") }}
                            <select
                                v-model="filtres.equipement"
                                class="rounded border border-surface-300 px-2 py-1 text-sm"
                                data-test="filtre-equipement"
                            >
                                <option value="tous">
                                    {{
                                        t("alarmesV2.catalogue.tousEquipements")
                                    }}
                                </option>
                                <option
                                    v-for="g in EQUIPEMENTS"
                                    :key="g"
                                    :value="g"
                                >
                                    {{
                                        t(
                                            `alarmesV2.catalogue.equipements.${g}`
                                        )
                                    }}
                                </option>
                            </select>
                        </label>
                        <label class="flex flex-col text-xs">
                            {{ t("alarmesV2.catalogue.trier") }}
                            <select
                                v-model="filtres.tri"
                                class="rounded border border-surface-300 px-2 py-1 text-sm"
                                data-test="tri"
                            >
                                <option value="alpha">
                                    {{ t("alarmesV2.catalogue.triAlpha") }}
                                </option>
                                <option value="a_traiter">
                                    {{ t("alarmesV2.catalogue.triATraiter") }}
                                </option>
                            </select>
                        </label>
                    </div>
                    <p
                        v-if="getTotal > 0"
                        class="text-xs text-surface-600"
                        aria-live="polite"
                        data-test="compte"
                    >
                        {{ getCompte }}
                    </p>
                </section>

                <!-- ================================ -->
                <!-- LES CARTES -->
                <!-- ================================ -->
                <p
                    v-if="getVide === 'rien_publie'"
                    class="text-sm text-surface-600"
                    data-test="vide"
                >
                    {{ t("alarmesV2.catalogue.vide") }}
                </p>
                <p
                    v-else-if="getVide === 'rien_propose'"
                    class="text-sm text-surface-600"
                    data-test="vide-equipement"
                >
                    {{ t("alarmesV2.catalogue.videEquipement") }}
                </p>
                <p
                    v-else-if="getTotal > 0 && getAffiches.length === 0"
                    class="text-sm text-surface-600"
                    data-test="aucun-resultat"
                >
                    {{ t("alarmesV2.catalogue.aucunResultat") }}
                </p>
                <div
                    v-else-if="getTotal > 0"
                    class="grid gap-4 md:grid-cols-2 xl:grid-cols-3"
                >
                    <CarteTypeAlarme
                        v-for="ty in getAffiches"
                        :key="ty.code"
                        :type="ty"
                    />
                </div>
            </template>
        </div>
    </CadreModule>
</template>
